/**
 * What the map and the scenario lab need on top of the shared position
 * vocabulary in `../components/format`.
 *
 * The words, colours and chevron shapes come from there, so the board, the map
 * and a projection all say the same thing. What is added here is what only
 * these two views have to deal with: an actor with *no* recorded position, a
 * continuous (projected) value rather than one of the five steps, and the
 * unread slice of the balance bar.
 */

import { balanceShares, positionLabel as baseLabel, positionStyle } from '../components/format';
import type { PositionStyle } from '../components/format';
import type { BalanceOfForces } from '../../../types/issues';

export type { PositionStyle };
export { positionStyle };
export { POSITION_ORDER, formatIndex, indexOffset, subjectHref, subjectKey } from '../components/format';

/** A hole in what we know, which is not the same as a neutral */
export const UNKNOWN_LABEL = 'No position recorded';
export const UNKNOWN_HEX = '#a3a3a3';

export const isUnknown = (value: number | null | undefined): value is null | undefined =>
  value === null || value === undefined || Number.isNaN(value as number);

/** 'Supportive', or 'No position recorded' when nothing is on file */
export function labelOf(value: number | null | undefined): string {
  return isUnknown(value) ? UNKNOWN_LABEL : baseLabel(value as number);
}

/** Fill colour; white for the unrecorded, which are drawn as an outline */
export function hexOf(value: number | null | undefined): string {
  return isUnknown(value) ? '#ffffff' : positionStyle(value as number).hex;
}

export function signed(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  if (rounded === 0) return '0';
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded)}`;
}

/**
 * 'Supportive (+0.6)' — a projection lands between the five steps, and hiding
 * the remainder would overstate how far someone actually moved.
 */
export function detailOf(value: number | null | undefined): string {
  if (isUnknown(value)) return UNKNOWN_LABEL;
  const exact = Math.round((value as number) * 10) / 10;
  return Number.isInteger(exact) ? labelOf(value) : `${labelOf(value)} (${signed(exact)})`;
}

/** Which side of the line a value sits on; near-zero is nobody's */
export function camp(value: number | null | undefined): 'support' | 'oppose' | 'undecided' {
  if (isUnknown(value)) return 'undecided';
  const v = value as number;
  if (v > 0.25) return 'support';
  if (v < -0.25) return 'oppose';
  return 'undecided';
}

/**
 * Influence figures arrive either 0–100 or 0–1 depending on where they were
 * set; normalise to 0–1 so node sizes stay comparable.
 */
export function influenceOf(issueInfluence: number | null | undefined, directoryInfluence?: number | null): number {
  const issue = Number.isFinite(issueInfluence as number) ? (issueInfluence as number) : 0;
  const directory = Number.isFinite(directoryInfluence as number) ? (directoryInfluence as number) || 0 : 0;
  const raw = issue > 0 ? issue : directory;
  if (raw <= 0) return 0;
  return raw > 1 ? Math.min(1, raw / 100) : Math.min(1, raw);
}

/* ------------------------------------------------------------------ */
/* Balance of forces                                                   */
/* ------------------------------------------------------------------ */

export interface BalanceSegment {
  key: 'oppose' | 'neutral' | 'support' | 'unknown';
  label: string;
  /** 0–100 share of all the influence in play, unread weight included */
  pct: number;
  hex: string;
  hatched: boolean;
}

/**
 * The bar the whole map hangs under, in the same order as the positions board:
 * opposition (hatched) on the left, support on the right. Unread influence is
 * shown alongside rather than folded away — an actor nobody has read is a risk,
 * not an absence.
 */
export function balanceSegments(balance: BalanceOfForces | null | undefined): BalanceSegment[] {
  const shares = balanceShares(balance);
  const unknown = Math.max(0, balance?.unknownInfluence ?? 0);
  const known = Math.max(0, balance?.support ?? 0) + Math.max(0, balance?.neutral ?? 0) + Math.max(0, balance?.oppose ?? 0);
  const total = known + unknown;
  const scale = total > 0 ? known / total : 0;
  return [
    { key: 'oppose', label: 'Against', pct: shares.oppose * scale, hex: positionStyle(-2).hex, hatched: true },
    { key: 'neutral', label: 'Undecided', pct: shares.neutral * scale, hex: positionStyle(0).hex, hatched: false },
    { key: 'support', label: 'With us', pct: shares.support * scale, hex: positionStyle(2).hex, hatched: false },
    { key: 'unknown', label: 'Unread', pct: total > 0 ? (unknown / total) * 100 : 0, hex: '#e5e5e5', hatched: true },
  ];
}

/** One sentence for the index, so the number never stands on its own */
export function describeIndex(index: number): string {
  const i = Number.isFinite(index) ? index : 0;
  if (i >= 45) return 'Strongly with us';
  if (i >= 15) return 'Leaning our way';
  if (i > -15) return 'Finely balanced';
  if (i > -45) return 'Leaning against us';
  return 'Strongly against us';
}

/** How lopsided the influence is, as words */
export function describeConcentration(concentration: number): string {
  const c = Number.isFinite(concentration) ? concentration : 0;
  if (c >= 0.6) return 'A few actors hold most of the weight';
  if (c >= 0.35) return 'Weight sits with a handful of actors';
  return 'Weight is spread widely';
}
